import { Menu } from '@mui/material'
import React, { ComponentType, createContext, Ref, useContext, useRef, useState } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { FeatureFlagsEnum, RequestTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { RequestDetail } from '@omnea-app/requests/requestsFrontendClient'
import { useRequestDetailContext } from '../../context/RequestDetailContext'
import { useCanPerformRequestAction, CanPerformActionsRequest } from '../../helpers/useCanPerformRequestAction'
import { AuditLogMenuItem } from './buttons/audit-log'
import { DeleteMenuItem } from './buttons/delete'
import { DuplicateRequestMenuItem } from './buttons/duplicateRequest'
import { MuteMenuItem } from './buttons/mute'
import { PauseMenuItem } from './buttons/pause'
import { RejectMenuItem } from './buttons/reject'
import { ReopenMenuItem } from './buttons/reopen'
import { ResumeMenuItem } from './buttons/resume'
import { ToggleHiddenStepsMenuItem } from './buttons/toggle-hidden-steps'

const ManageMenuContext = createContext<{ closeMenu: () => void } | null>(null)

export const useManageMenuClose = () => {
  const context = useContext(ManageMenuContext)
  return context?.closeMenu
}

type Origin = {
  horizontal: 'center' | 'left' | 'right'
  vertical: 'center' | 'top' | 'bottom'
}

// Anchors must not be defined inline in renders to prevent the menu changing position
export type ManageMenuAnchor = ComponentType<{ buttonRef: Ref<HTMLButtonElement>; openMenu: () => void }>

// This can be RequestDetail or RequestHeaderData, so just the required properties are defined here
export type ManageMenuRequest = {
  rawData: {
    completedAt: string | null
    state: string | null
    type: string
    name?: string
    requestorUserId: string
  }
  derivedFields: {
    ownerId: string | null
    currentUserAssignees: Pick<RequestDetail['derivedFields']['currentUserAssignees'][number], 'id'>[]
    currentGroupAssignees: RequestDetail['derivedFields']['currentGroupAssignees']
  }
} & CanPerformActionsRequest

type Props = {
  requestId: string
  request?: ManageMenuRequest
  onRejectRequest: (reason: string) => void
  onUnrejectRequest: (reason: string, productId?: string) => void
  onDeleteRequest: () => void
  anchor: ManageMenuAnchor
  anchorOrigin?: Origin
  transformOrigin?: Origin
}

export const ManageMenu = ({
  requestId,
  request,
  onDeleteRequest,
  onRejectRequest,
  onUnrejectRequest,
  anchor: Anchor,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'right',
  },
}: Props) => {
  const { user } = useAuthenticatedUser()
  const p2pAdminMode = useFeatureFlags()[FeatureFlagsEnum.P2P_ADMIN_MODE]
  const { showHiddenSteps, toggleHiddenSteps } = useRequestDetailContext()
  const canPerformRequestAction = useCanPerformRequestAction(request)

  const menuAnchorEl = useRef<HTMLButtonElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const openMenu = () => setMenuOpen(true)
  const closeMenu = () => setMenuOpen(false)

  const isRequestCompleted = request?.rawData?.completedAt != null
  const isRequestRejected = request?.rawData?.state === 'REJECTED'
  const isRequestPaused = request?.rawData?.state === 'PAUSED'

  const requestType = request?.rawData.type

  const showRejectUnrejectButton = user.isNonViewerAdmin && request && !isRequestCompleted
  const showReopenButton = user.isNonViewerAdmin && request?.rawData.completedAt != null
  const showResumeButton = isRequestPaused && canPerformRequestAction('resumeRequest')
  const showPauseButton = request?.rawData.state === null && !isRequestCompleted && canPerformRequestAction('pauseRequest')
  // P2P_ADMIN_MODE (frontend-only HubSpot stop-gap): admins without config permission cannot delete requests.
  const showDeleteButton = user.isNonViewerAdmin && (!p2pAdminMode || user.hasConfigPermissions)
  const showToggleHiddenStepsButton = user.isAdmin
  const showAuditLog = !user.isSupplier
  const showDuplicateButton =
    !user.isSupplier && [RequestTypeEnum.NEW_PURCHASE, RequestTypeEnum.ACCESS_REQUEST].includes(requestType as RequestTypeEnum)

  return (
    <>
      <Anchor buttonRef={menuAnchorEl} openMenu={openMenu} />
      <Menu
        open={menuOpen}
        anchorEl={menuAnchorEl.current}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        onClose={closeMenu}
        keepMounted // Children contain modals that should remain mounted when the menu is closed
        slotProps={{ paper: { sx: { mt: 2 } } }}
      >
        {showPauseButton && <PauseMenuItem requestId={requestId} onClose={closeMenu} />}
        {showResumeButton && <ResumeMenuItem requestId={requestId} onClose={closeMenu} />}
        {showRejectUnrejectButton && (
          <RejectMenuItem
            onReject={onRejectRequest}
            onUnreject={onUnrejectRequest}
            rejectMode={!isRequestRejected}
            onClose={closeMenu}
          />
        )}
        {showReopenButton && <ReopenMenuItem requestId={requestId} onClose={closeMenu} />}
        {showDuplicateButton && (
          <DuplicateRequestMenuItem requestId={requestId} requestName={request?.rawData.name ?? undefined} onClose={closeMenu} />
        )}
        {showAuditLog && <AuditLogMenuItem requestId={requestId} />}
        <MuteMenuItem requestId={requestId} onClose={closeMenu} />
        {showToggleHiddenStepsButton && (
          <ToggleHiddenStepsMenuItem
            showHiddenSteps={showHiddenSteps}
            onToggleHiddenSteps={toggleHiddenSteps}
            onClose={closeMenu}
          />
        )}
        {/* Delete button should always be at the bottom */}
        {showDeleteButton && (
          <DeleteMenuItem
            onDelete={onDeleteRequest}
            isRenewal={requestType === RequestTypeEnum.RENEWAL}
            isPaused={isRequestPaused}
            onClose={closeMenu}
          />
        )}
      </Menu>
    </>
  )
}
